import { useNavigate, useSearchParams } from "react-router-dom"
import { TournamentFields } from "./tournament-fields"
import styles from "./tournament-page.module.css"
import { useCallback, useEffect, useState } from "react"
import { TournamentManipulationService } from "shared/tournaments/services/tournament-manipulation-service"
import { useDispatch, useSelector } from "react-redux"
import { setEditableTournamentFields } from "app/store/slices/editableTournament"
import { redirectIfNoRefreshToken } from "shared/redirects"
import { Box, Tabs, Tab, CircularProgress } from "@mui/material"
import { ETournamentPageViewRegimes } from "../types"
import { TournamentPlayers } from "./tournament-players"
import { RootState } from "app/store"
import { showErrorMessage, clearErrorMessage, showInfoMessage, clearInfoMessage } from "app/store/slices/alerts"
import { IconMenu } from "widgets/icon-menu"
import { TTournamentDeleteMessage } from "pages/TournamentsListPage/types"
import { TournamentGrid } from "./tournament-grid"

const tournamentManipulationService = TournamentManipulationService()

export const TournamentPage = () => {
    const [tournamentPageViewRegime, setTournamentPageViewRegime] = useState<ETournamentPageViewRegimes>(ETournamentPageViewRegimes.Info)
    const [searchParams] = useSearchParams()
    const tournamentId = searchParams.get('id')
    const navigate = useNavigate()
    const [loadingTour, setLoadingTour] = useState<boolean>(false)
    const dispatch =  useDispatch()

    const editableTournament = useSelector((state: RootState) => state.editableTournamentSlice)

    const loadTournament = async () => {
        if(!tournamentId){
            return
        }

        setLoadingTour(true)

        const result = await tournamentManipulationService.getTournament(tournamentId)
        if(!result.errorMessage){ 
            dispatch(setEditableTournamentFields({
                ...result.tournament,
            }))

            if(result.tournament.isEllimination){
                dispatch(setEditableTournamentFields({
                    gridFirstSize: result.tournament.tours[0]?.length || 8,
                    gridLastSize: result.tournament.tours.at(-1)?.length || 2
                }))
            }
        }

        setLoadingTour(false)
    }

    useEffect(() => {
        loadTournament()
        redirectIfNoRefreshToken(navigate)
    }, [])

    const changeTab = useCallback((targetRegime: ETournamentPageViewRegimes) => {
        setTournamentPageViewRegime(targetRegime)
    }, [])

    const saveTournamentToLocalStorage = async () => {    
        if(!editableTournament.canSave){
            dispatch(showErrorMessage('Заполните все необходимые поля'))
            setTimeout(() => { dispatch(clearErrorMessage()) }, 5000)
            return
        }
    
        const tms = TournamentManipulationService()
    
        if (tournamentId) {
            const {errorMessage, tournament}: any = await tms.updateTournament(editableTournament)
            
            if(errorMessage){
                dispatch(showErrorMessage(`Ошибка обновления турнира: ${errorMessage}`))
                setTimeout(() => { dispatch(clearErrorMessage()) }, 5000) 
                return
            }

            dispatch(setEditableTournamentFields(tournament))
            dispatch(showInfoMessage(`Турнир успешно обновлен`))
            setTimeout(() => { dispatch(clearInfoMessage()) }, 5000)
            loadTournament()
    
        } else {
            const creationResult: any = await tms.createTournament(editableTournament)

            if(creationResult.errorMessage){
                dispatch(showErrorMessage(`Ошибка создания турнира: ${creationResult.errorMessage}`))
                setTimeout(() => { dispatch(clearErrorMessage()) }, 5000) 
                return
            }
            
            dispatch(setEditableTournamentFields(creationResult.tournament))
            navigate(window.location.search + '?id=' + creationResult.tournament.id)
            dispatch(showInfoMessage(`Турнир ${creationResult.tournament.id} успешно создан`))
            setTimeout(() => { dispatch(clearInfoMessage()) }, 5000)
        }
    }

    const deleteTournament = useCallback(async () => {
        if (!tournamentId) {
            dispatch(showErrorMessage("Отсутствует ID турнира"))
            setTimeout(() => { dispatch(clearErrorMessage()) }, 5000); return
        }
    
        const confirmDelete = confirm(`Вы уверены, что хотите удалить турнир <${tournamentId}> ?`)
        if (!confirmDelete) {
            return
        }
    
        const tournamentDeleteMessage: TTournamentDeleteMessage = await tournamentManipulationService.removeTournament(tournamentId || '')
        if (tournamentDeleteMessage.errorMessage) {
            dispatch(showErrorMessage(tournamentDeleteMessage.errorMessage))
            setTimeout(() => { dispatch(clearErrorMessage()) }, 5000); return
        }
        dispatch(showInfoMessage(`Турнир ${tournamentId} удален`))
        navigate('/tournaments')
        setTimeout(() => { dispatch(clearInfoMessage()) }, 5000)
    }, [tournamentId])

    return (
        <div className={styles.App}>
            <div className={styles.Icons}>
                <IconMenu
                    onSearchClick={() => navigate('/tournaments')}
                    onDeleteClick={tournamentId ? () => deleteTournament() : undefined}
                    onSaveClick={saveTournamentToLocalStorage}
                />
            </div>
            <div className={styles.Content}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: "white", borderRadius: 12}}>
                    <Tabs value={tournamentPageViewRegime} onChange={(_, targetValue) => changeTab(targetValue)} aria-label="basic tabs example">
                        <Tab value={ETournamentPageViewRegimes.Info} label="Информация о турнире" />
                        <Tab value={ETournamentPageViewRegimes.Players} label="Игроки" />
                        <Tab value={ETournamentPageViewRegimes.Grid} label="Турнирная сетка" />
                    </Tabs>
                </Box>

                {tournamentPageViewRegime === ETournamentPageViewRegimes.Info
                && <TournamentFields/>}

                {tournamentPageViewRegime === ETournamentPageViewRegimes.Players
                && <TournamentPlayers/>}

                {tournamentPageViewRegime === ETournamentPageViewRegimes.Grid
                && <TournamentGrid/>}
            </div>

            {loadingTour && <div className={styles.loading}><CircularProgress size="4rem" /></div>}
        </div>
    )
}